import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import './Contact.css';
const Contact = () => {
    const contactSectionRef = useRef(null);
    const recaptchaRef = useRef(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
    });
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    useEffect(() => {
        const observerOptions = {
            threshold: 0.2,
            rootMargin: '0px 0px -100px 0px',
        };
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('flipbook-visible');
                }
            });
        }, observerOptions);
        if (contactSectionRef.current) {
            observer.observe(contactSectionRef.current);
        }
        return () => observer.disconnect();
    }, []);
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };
    const handlePhoneInputChange = (e) => {
        const value = e.target.value;
        // Remove all non-digits to get the raw number
        const digitsOnly = value.replace(/\D/g, '');
        // Limit to 10 digits
        const limitedDigits = digitsOnly.substring(0, 10);
        // Format the phone number
        let formatted = limitedDigits;
        if (limitedDigits.length >= 1) {
            formatted = '(' + limitedDigits.substring(0, 3);
        }
        if (limitedDigits.length >= 4) {
            formatted = '(' + limitedDigits.substring(0, 3) + ') ' + limitedDigits.substring(3);
        }
        if (limitedDigits.length >= 7) {
            formatted = '(' + limitedDigits.substring(0, 3) + ') ' + limitedDigits.substring(3, 6) + '-' + limitedDigits.substring(6);
        }
        setFormData({
            ...formData,
            phone: formatted,
        });
    };
    const handleRecaptchaChange = (token) => {
        setRecaptchaToken(token);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!recaptchaToken) {
            setSubmitError('Please complete the reCAPTCHA verification.');
            return;
        }
        setIsSubmitting(true);
        setSubmitError(null);
        setSubmitSuccess(false);
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
            const response = await fetch(`${apiUrl}/contacts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, recaptchaToken }),
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || data.errors?.[0]?.msg || 'Failed to submit form');
            }
            setSubmitSuccess(true);
            setFormData({ name: '', email: '', phone: '', company: '', message: '' });
            setRecaptchaToken(null);
            recaptchaRef.current?.reset();
            // Scroll to success message
            setTimeout(() => {
                const successElement = document.querySelector('.contact-success-message');
                if (successElement) {
                    successElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        }
        catch (error) {
            console.error('Error submitting form:', error);
            const errorMessage = error instanceof Error ? error.message : 'There was an error sending your message. Please try again.';
            setSubmitError(errorMessage);
        }
        finally {
            setIsSubmitting(false);
        }
    };
    return (_jsx("section", { id: "contact", ref: contactSectionRef, className: "contact section flipbook-section", children: _jsxs("div", { className: "container", children: [_jsx("h2", { className: "section-title", children: "Let's Get Started" }), _jsxs("div", { className: "contact-content", children: [_jsxs("div", { className: "contact-intro", children: [_jsx("h3", { children: "Ready to Transform Your Business?" }), _jsx("p", { children: "Stop wasting time on manual processes. Let's build intelligent, automated solutions that drive real results." }), _jsxs("div", { className: "contact-methods", children: [_jsxs("div", { className: "contact-method", children: [_jsx("div", { className: "contact-method-icon", children: "\uD83D\uDCE7" }), _jsxs("div", { className: "contact-method-info", children: [_jsx("h4", { children: "Email Us" }), _jsx("a", { href: "mailto:info@buzbiz.com", children: "info@buzbiz.com" })] })] }), _jsxs("div", { className: "contact-method", children: [_jsx("div", { className: "contact-method-icon", children: "\uD83D\uDCDE" }), _jsxs("div", { className: "contact-method-info", children: [_jsx("h4", { children: "Call Us" }), _jsx("a", { href: "tel:+16504853370", children: "(650) 485-3370" })] })] }), _jsxs("div", { className: "contact-method", children: [_jsx("div", { className: "contact-method-icon", children: "\uD83D\uDCC5" }), _jsxs("div", { className: "contact-method-info", children: [_jsx("h4", { children: "Schedule a Call" }), _jsx("a", { href: "https://calendly.com/buzbiz/30minconsult", target: "_blank", rel: "noopener noreferrer", children: "Book a 30-Minute Consultation" })] })] })] })] }), _jsxs("div", { className: "contact-form-wrapper", children: [_jsx("div", { className: "contact-form-logo", children: _jsx("img", { src: "/images/BuzBiz-h-bg-v2.png", alt: "BuzBiz Digital Business Solutions Logo", width: "200", height: "50" }) }), submitSuccess ? (_jsxs("div", { className: "contact-success-message", children: [_jsx("div", { className: "success-icon", children: "\u2713" }), _jsx("h3", { children: "Thank you!" }), _jsx("p", { children: "Your message has been sent. We'll get back to you soon." }), _jsx("button", { type: "button", className: "btn btn-primary", onClick: () => {
                                                setSubmitSuccess(false);
                                                setSubmitError(null);
                                            }, children: "Send Another Message" })] })) : (_jsxs("form", { className: "contact-form", onSubmit: handleSubmit, children: [submitError && (_jsx("div", { className: "contact-error-message", children: submitError })), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "name", children: "Name" }), _jsx("input", { type: "text", id: "name", name: "name", value: formData.name, onChange: handleChange, required: true })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "email", children: "Email" }), _jsx("input", { type: "email", id: "email", name: "email", value: formData.email, onChange: handleChange, required: true })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "phone", children: "Phone" }), _jsx("input", { type: "tel", id: "phone", name: "phone", value: formData.phone, onChange: handlePhoneInputChange, placeholder: "(555) 123-4567" })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "company", children: "Company" }), _jsx("input", { type: "text", id: "company", name: "company", value: formData.company, onChange: handleChange })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "message", children: "What challenges are you facing?" }), _jsx("textarea", { id: "message", name: "message", rows: 5, value: formData.message, onChange: handleChange, required: true })] }), _jsx("div", { className: "form-group recaptcha-container", children: _jsx(ReCAPTCHA, { ref: recaptchaRef, sitekey: process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', onChange: handleRecaptchaChange }) }), _jsx("button", { type: "submit", className: "btn btn-primary btn-large", disabled: isSubmitting || !recaptchaToken, children: isSubmitting ? 'Sending...' : 'Send Message' })] }))] })] })] }) }));
};
export default Contact;
